import React, { useState, useRef, useLayoutEffect, useEffect } from "react";
import FilterBar from "../UI/FilterBar";
import SearchInputTable from "../common/SearchInputTable";
import styles from "../../_css/UsersTable.module.css";
import {
  MoreVertical,
  User,
  Trash2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import SelectComponentWithTabs from "../common/SelectComponentWithTabs";
import UserProfileModal from "../UI/UserProfileModal";
import DeleteUserModal from "../UI/DeleteUserModal";
import { createPortal } from "react-dom";
import _ from "lodash";
import { formatDate } from "../helper/formatters";
import { deleteUsers } from "../../requests";
import {
  deleteAuthToken,
  notifyError,
  notifySuccess,
} from "../../utils/helper";
import { useDispatch } from "react-redux";
import { startLoader, stopLoader } from "../../_store/reducers/loaderReducer";

const PAGE_SIZE = 5;
const TOTAL_ENTRIES = 20;
const DROPDOWN_WIDTH = 180; // px

const MESSAGE = {
  single:
    "Are you sure you want to delete this account? This action is permanent and cannot be undone.",
  multiple:
    "More than 1 user accounts are selected. Are you sure you want to delete these accounts? This action is permanent and cannot be undone.",
};

// Custom styles copied from SelectComponent
const dropdownMenuStyles = {
  position: "absolute",
  top: "36px",
  right: "0",
  minWidth: "180px",
  width: "180px",
  background: "#fff",
  borderRadius: "12px",
  boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
  zIndex: 10,
  padding: "6px 0",
  border: "1px solid #eaf6ff",
};
const dropdownOptionStyles = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  padding: "10px 18px",
  fontSize: "13px",
  color: "#174291",
  background: "#F2F9FD",
  border: "none",
  borderRadius: "8px",
  cursor: "pointer",
  margin: "4px 8px",
  transition: "background 0.2s",
};
const dropdownOptionHover = {
  background: "#EFFCFB",
};

const groupedOptions = [
  {
    label: "By Status",
    options: [
      { value: "Active", label: "Active" },
      { value: "Expired", label: "Expired" },
    ],
  },
  {
    label: "By Subscription",
    options: [
      { value: "Free", label: "Free User" },
      { value: "Premium", label: "Premium User" },
    ],
  },
  {
    label: "By Language",
    options: [
      { value: "Arabic", label: "Arabic User" },
      { value: "English", label: "English User" },
    ],
  },
];

const groupedOptions2 = [
  {
    label: "By Name",
    options: [
      { value: "Name ASC", label: "Name: A to Z" },
      { value: "Name DESC", label: "Name: Z to A" },
    ],
  },
  {
    label: "By Sessions",
    options: [
      { value: "Sessions DESC", label: "High to Low" },
      { value: "Sessions ASC", label: "Low to High" },
    ],
  },
];

function RowActionsDropdown({ onViewProfile, onRemoveUser }) {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(null);
  const btnRef = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownPos, setDropdownPos] = useState({
    top: 0,
    left: 0,
    width: 180,
  });

  useLayoutEffect(() => {
    if (open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + window.scrollY,
        left: rect.right + window.scrollX - DROPDOWN_WIDTH,
      });
    }
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    function handleClick(e) {
      if (btnRef.current && btnRef.current.contains(e.target)) {
        return;
      }
      if (dropdownRef.current && dropdownRef.current.contains(e.target)) {
        return;
      }
      setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <button
        className={styles.rowActionsBtn}
        onClick={() => setOpen((o) => !o)}
        tabIndex={0}
        aria-label="Row actions"
        style={{
          background: "none",
          border: "none",
          padding: 0,
          cursor: "pointer",
        }}
        ref={btnRef}
      >
        <MoreVertical size={22} color="#798797" />
      </button>
      {open &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{
              ...dropdownMenuStyles,
              position: "absolute",
              top: dropdownPos.top,
              left: dropdownPos.left,
              minWidth: "180px",
              width: "180px",
              zIndex: 9999,
            }}
          >
            <div
              style={
                hovered === 0
                  ? { ...dropdownOptionStyles, ...dropdownOptionHover }
                  : dropdownOptionStyles
              }
              onMouseEnter={() => setHovered(0)}
              onMouseLeave={() => setHovered(null)}
              tabIndex={0}
              onClick={() => {
                setOpen(false);
                onViewProfile && onViewProfile();
              }}
            >
              <User size={18} style={{ color: "#174291" }} /> View Profile
            </div>
            <div
              style={
                hovered === 1
                  ? { ...dropdownOptionStyles, ...dropdownOptionHover }
                  : dropdownOptionStyles
              }
              onMouseEnter={() => setHovered(1)}
              onMouseLeave={() => setHovered(null)}
              tabIndex={0}
              onClick={() => {
                setOpen(false);
                onRemoveUser && onRemoveUser();
              }}
            >
              <Trash2 size={18} style={{ color: "#174291" }} /> Remove User
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

const UsersTable = ({
  headers,
  rows,
  searchConfig,
  variant = "blue",
  handleFilterChange,
  handleSearchChange,
  handleDateFilterChange,
  handleSorting,
  pagination,
  searchValue,
  onPageChange,
  dateFilter,
}) => {
  const [page, setPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]); // store indices of selected rows
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [deleteTargetIdx, setDeleteTargetIdx] = useState([]);

  const dispatch = useDispatch();

  useEffect(() => {
    console.log(deleteTargetIdx);
  }, [deleteTargetIdx]);

  // Pagination logic
  const pagedRows = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const totalPages = pagination?.totalPages || 1;
  const totalEntries = pagination?.totalItems || rows.length;
  const currentPage = pagination?.currentPage || 1;
  const itemsPerPage = pagination?.itemsPerPage || PAGE_SIZE;

  // Map pagedRows to their global indices
  const pagedRowIndices = pagedRows.map(
    (_, idx) => (page - 1) * PAGE_SIZE + idx
  );

  const allVisibleSelected = pagedRowIndices.every((idx) =>
    selectedRows.includes(idx)
  );
  const someVisibleSelected = pagedRowIndices.some((idx) =>
    selectedRows.includes(idx)
  );

  const handleSelectAll = () => {
    if (allVisibleSelected) {
      setDeleteTargetIdx([]);
      setSelectedRows(
        selectedRows.filter((idx) => !pagedRowIndices.includes(idx))
      );
    } else {
      setDeleteTargetIdx(rows.map((row) => row.id));
      setSelectedRows([
        ...selectedRows.filter((idx) => !pagedRowIndices.includes(idx)),
        ...pagedRowIndices,
      ]);
    }
  };

  const handleSelectRow = (rowIdx, rowId) => {
    if (deleteTargetIdx.includes(rowId)) {
      setDeleteTargetIdx(deleteTargetIdx.filter((id) => id !== rowId));
    } else {
      setDeleteTargetIdx([...deleteTargetIdx, rowId]);
    }
    if (selectedRows.includes(rowIdx)) {
      setSelectedRows(selectedRows.filter((idx) => idx !== rowIdx));
    } else {
      setSelectedRows([...selectedRows, rowIdx]);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && onPageChange) {
      onPageChange(newPage, itemsPerPage);
    }
  };
  const handleSelectDelete = (rowId) => {
    if (deleteTargetIdx.length === 0 && !deleteTargetIdx.includes(rowId)) {
      return [rowId];
    }
    return deleteTargetIdx;
  };
  const formatColumnData = (data, key) => {
    // TODO: Add more keys to the arrays
    const currencyKeys = [
      "openai_cost",
      "elevenlab_cost",
      "total_spend",
      "total_cost",
      "avg_spend_per_user",
    ];
    const capitalizeKeys = ["language", "sessions", "top_session", "sub_type"];

    // TODO: Add logic for keys
    if (key === "date_joined") return formatDate(data);
    if (capitalizeKeys.includes(key)) return _.capitalize(data);
    if (currencyKeys.includes(key)) return `$${data || 0}`;
    if (key === "avg_duration_minutes") return `${data} min`;
    if (key === "subscription_status")
      return _.lowerCase(data) === "active" ? (
        <span className={styles.activeStatusBadge}>Active</span>
      ) : (
        <span className={styles.expiredStatusBadge}>Expired</span>
      );

    return data;
  };

  const handleOnDeleteClick = async () => {
    setDeleteModalOpen(false);
    dispatch(startLoader());
    try {
      const response = await deleteUsers({ user_ids: deleteTargetIdx });
      notifySuccess(response?.data?.message);
      window.location.reload();
    } catch (error) {
      if (error?.response?.status === 401) {
        deleteAuthToken();
        window.location.reload();
      }
      notifyError(error?.response?.data?.message);
      console.log(error);
    }
    dispatch(stopLoader());
  };

  return (
    <div className={styles.tableContainer}>
      <div className={styles.filtersRow}>
        <div className="d-flex align-items-center gap-3">
          <SelectComponentWithTabs
            options={groupedOptions}
            placeholder="Select Filter By"
            handleValueChange={handleFilterChange}
            disabled={false}
            isMulti={false}
            isClearable={false}
            value={null}
          />
          <SelectComponentWithTabs
            options={groupedOptions2}
            placeholder="Select Sort By"
            handleValueChange={handleSorting}
            disabled={false}
            isMulti={false}
            isClearable={false}
            value={null}
          />
          <FilterBar
            onFilterChange={handleDateFilterChange}
            showExtraFilter={false}
            filter={dateFilter}
          />
        </div>
        <SearchInputTable
          value={searchValue}
          onChange={handleSearchChange}
          placeholder={searchConfig.placeholder}
          column={searchConfig.column}
          padding="0px 8px"
          paddingContainer="0 8px 0 0"
        />
      </div>
      <hr />
      <div className={styles.tableWrapper}>
        <div className={styles.tableBorderWrapper}>
          <table className={styles.usersTable}>
            <thead>
              <tr
                style={{
                  background: variant === "blue" ? "#F3FAFF" : "#F0FDF4",
                }}
              >
                <th
                  style={{
                    width: "12px",
                    color: variant === "blue" ? "#174291" : "#14532B",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    ref={(el) => {
                      if (el)
                        el.indeterminate =
                          !allVisibleSelected && someVisibleSelected;
                    }}
                    onChange={handleSelectAll}
                    className={styles.checkbox}
                  />
                  <label style={{ marginLeft: "8px" }}>S.L</label>
                </th>

                {headers.map((header, idx) => (
                  <th
                    style={{
                      width: ["Action"].includes(header.label)
                        ? "12px"
                        : "auto",
                      color: variant === "blue" ? "#174291" : "#14532B",
                    }}
                    key={idx}
                  >
                    {header.label}
                  </th>
                ))}
                <th
                  style={{
                    width: "12px",
                    color: variant === "blue" ? "#174291" : "#14532B",
                  }}
                >
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => {
                const globalIdx = (page - 1) * 5 + idx;
                return (
                  <tr key={idx}>
                    <td className={styles.tableCell} style={{ width: "12px" }}>
                      <input
                        type="checkbox"
                        checked={selectedRows.includes(globalIdx)}
                        onChange={() => handleSelectRow(globalIdx, row.id)}
                        className={styles.checkbox}
                      />
                      <span style={{ marginLeft: "8px" }}>{globalIdx + 1}</span>
                    </td>
                    {headers.map((header, idxIn) => (
                      <td key={idxIn} className={styles.tableCell}>
                        {formatColumnData(row[header.key], header.key)}
                      </td>
                    ))}
                    <td>
                      <RowActionsDropdown
                        onViewProfile={() => {
                          setSelectedProfile(row);
                          setProfileModalOpen(true);
                        }}
                        onRemoveUser={() => {
                          setDeleteTargetIdx(handleSelectDelete(row.id));
                          setDeleteModalOpen(true);
                        }}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <div className="d-flex justify-content-between align-items-center pb-3 mt-3">
        <div className={styles.tableFooter}>
          Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
          {Math.min(currentPage * itemsPerPage, totalEntries)} of {totalEntries}{" "}
          entries
        </div>
        <div className={styles.paginationRow}>

          <div className="d-flex align-items-center gap-2">
            <button
              className={styles.paginationBtn}
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <ChevronLeft size={18} />
            </button>
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i + 1}
                className={
                  currentPage === i + 1
                    ? `${styles.paginationBtn} ${styles.paginationBtnActive}`
                    : styles.paginationBtn
                }
                onClick={() => handlePageChange(i + 1)}
              >
                {i + 1 < 10 ? `0${i + 1}` : i + 1}
              </button>
            ))}
            <button
              className={styles.paginationBtn}
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      </div>

      <UserProfileModal
        show={profileModalOpen}
        onHide={() => setProfileModalOpen(false)}
        user={selectedProfile}
      />
      <DeleteUserModal
        show={deleteModalOpen}
        onHide={() => setDeleteModalOpen(false)}
        onDelete={handleOnDeleteClick}
        isMultiple={
          deleteTargetIdx.length > 1 &&
          deleteTargetIdx.includes(deleteTargetIdx)
        }
        count={deleteTargetIdx.length}
        btnText="Delete Account"
        title={"Remove User Account"}
        message={MESSAGE[deleteTargetIdx.length > 1 ? "multiple" : "single"]}
      />
    </div>
  );
};

export default UsersTable;
